import type { ReactNode } from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

/** Compact metric tile. Optionally clickable and/or with a pinned CTA. */
export function StatCard({
  icon,
  label,
  value,
  total,
  hint,
  accent,
  action,
  onClick,
  placeholder,
}: {
  icon: ReactNode;
  label: string;
  value?: number;
  total?: number;
  hint?: string;
  /** Tints the icon (not the value text) for at-a-glance status. */
  accent?: string;
  /** Optional element pinned to the bottom of the card (e.g. a CTA button). */
  action?: ReactNode;
  onClick?: () => void;
  /** Renders a muted "not wired up yet" state instead of a value. */
  placeholder?: string;
}) {
  const pct = total && total > 0 && value !== undefined ? Math.round((value / total) * 100) : null;
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: { xs: 2, sm: 2.5 },
        flex: 1,
        minWidth: { xs: 0, sm: 180 },
        display: 'flex',
        flexDirection: 'column',
        ...(onClick && {
          cursor: 'pointer',
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: 'rgba(255, 204, 0, 0.08)' },
        }),
      }}
    >
      <Stack spacing={1} sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: accent ?? 'text.secondary', display: 'flex' }}>{icon}</Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
        </Stack>
        {placeholder ? (
          <>
            <Typography variant="h3" component="div" sx={{ fontWeight: 600, lineHeight: 1, color: 'text.disabled' }}>
              —
            </Typography>
            <Typography variant="caption" color="text.disabled">{placeholder}</Typography>
          </>
        ) : (
          <>
            <Typography variant="h3" component="div" sx={{ fontWeight: 600, lineHeight: 1 }}>
              {(value ?? 0).toLocaleString()}
            </Typography>
            {pct !== null && (
              <Typography variant="caption" color="text.secondary">
                {pct}% of {total!.toLocaleString()}
              </Typography>
            )}
            {hint && pct === null && (
              <Typography variant="caption" color="text.secondary">{hint}</Typography>
            )}
          </>
        )}
      </Stack>
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Paper>
  );
}

export function SectionHeading({ title }: { title: string }) {
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontSize: { xs: '1.4rem', sm: 'h4.fontSize' } }}>{title}</Typography>
      <Divider />
    </Box>
  );
}

export interface ChartSeries {
  id: string;
  label: string;
  data: number[];
  color: string;
  area?: boolean;
  /** Same value across series stacks them (e.g. staff + architects = total DAU). */
  stack?: string;
}

/** A themed, shaded time-series line chart over a dense run of ISO dates. */
export function ActivityChart({
  title,
  subtitle,
  dates,
  series,
  emptyHint,
}: {
  title: string;
  subtitle: string;
  /** ISO yyyy-MM-dd, oldest first. */
  dates: string[];
  series: ChartSeries[];
  emptyHint?: string;
}) {
  const allValues = series.flatMap((s) => s.data);
  // Floor the axis so a quiet window still draws 0-at-the-bottom with
  // whole-number ticks, rather than a degenerate [0,0] domain.
  const maxY = Math.max(4, ...allValues);
  const isEmpty = allValues.every((v) => v === 0);
  const xData = dates.map((d) => new Date(`${d}T00:00:00Z`));
  // Translucent fills for any area series so overlaps/stacks stay readable.
  const areaSx = Object.fromEntries(
    series
      .filter((s) => s.area)
      .map((s) => [`& .MuiAreaElement-series-${s.id}`, { fillOpacity: 0.22 }])
  );

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>
      <Box sx={{ position: 'relative', width: '100%', overflowX: 'hidden' }}>
        <LineChart
          height={300}
          margin={{ left: 40, right: 16, top: 16, bottom: 24 }}
          xAxis={[
            {
              data: xData,
              scaleType: 'time',
              valueFormatter: (d: Date) =>
                d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
            },
          ]}
          yAxis={[{ min: 0, max: maxY, tickMinStep: 1 }]}
          series={series.map((s) => ({
            id: s.id,
            data: s.data,
            label: s.label,
            color: s.color,
            area: s.area,
            stack: s.stack,
            showMark: false,
            curve: 'monotoneX',
          }))}
          sx={areaSx}
        />
        {isEmpty && emptyHint && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {emptyHint}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
